// ---------------------------------------------------------------------------
// documentationV3Style — estilos da view Documentation V3 (Variante B).
//
// Estrutura:
//  1. Estilos de layout da view (rail + content + topbar/search).
//  2. Estilos de conteudo (.subtopic .doc-* .formula-*) — herdados do
//     V2 (`documentationStyle.js`) intactos, para garantir paridade
//     visual do conteudo HTML salvo em `SubTopic.content`.
//
// Decisao consciente de manter as classes `.doc-*` e `.formula-*` com
// nomes identicos ao V2: o conteudo HTML salvo no banco usa essas
// classes; renomear-las quebraria toda a doc V2 ja escrita.
//
// Origem do layout: `sandbox/documentation_v3_layouts/variant_b_hybrid.html`
// (prototipo validado na Wave 1a do Documentation V3).
// ---------------------------------------------------------------------------

import {
  cardTitle,
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";

const RAIL_WIDTH = 330;

// Tokens locais — paleta calibrada no prototipo HTML stand-alone
// (sandbox/documentation_v3_layouts/shared.css), validada na Wave 1a.
// Sao independentes dos indices de `grayColor` do tema porque a paleta
// padrao do app nao cobre 1:1 as nuances de cinza claro que essa view
// usa (linhas finas, fundos sutis, texto auxiliar).
const tones = {
  textPrimary: "#212121",
  textSecondary: "#424242",
  textMuted: "#757575",
  textFaint: "#9e9e9e",
  borderLine: "#e9ecef",
  surfaceSoft: "#fafafa",
  surfaceBg: "#f5f5f5",
  surfaceCard: "#ffffff",
};

const documentationV3Style = (theme) => ({
  // Page e' um flex-column que ocupa a viewport disponivel:
  //   - topCard (header com icone + titulo + busca) auto-altura
  //   - gap visual de 16px (separa header da linha inferior)
  //   - bottomRow (rail + conteudo) ocupa o resto, em flex-row
  // Essa arquitetura substituiu o Card-unico anterior porque o
  // CardIcon padrao da aplicacao depende de overflow:visible no Card,
  // o que conflitava com o clipping do scroll interno.
  pageContainer: {
    marginTop: "-19px",
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    height: "calc(100vh - 100px)",
  },
  topCard: {
    flex: "0 0 auto",
    margin: 0,
    // Mantemos `visible` por causa do CardIcon que sobressai do topo
    // do Card (marginTop: -10px no padrao do app).
    overflow: "visible",
  },
  bottomRow: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    gap: "8px",
  },
  railCard: {
    width: `${RAIL_WIDTH}px`,
    flexShrink: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "width 0.22s ease",
  },
  // Estado recolhido: a rail vira uma faixa fina que mostra apenas o
  // botao de reabrir. Largura suficiente para o IconButton respirar.
  railCardCollapsed: {
    width: "48px",
  },
  // Cabecalho da rail: abriga o botao "Inicio" (volta a tela de
  // boas-vindas) e o botao de recolher/expandir. Quando recolhido,
  // mostra apenas o toggle, centralizado.
  railHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
    padding: "6px 6px 6px 10px",
    borderBottom: `1px solid ${tones.borderLine}`,
    flexShrink: 0,
    minHeight: "44px",
    boxSizing: "border-box",
  },
  railHeaderCollapsed: {
    justifyContent: "center",
    padding: "6px 0",
  },
  railHomeBtn: {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: tones.textSecondary,
    padding: "4px 8px",
    minWidth: 0,
    "& .MuiButton-startIcon": {
      marginRight: "6px",
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "12",
      color: theme.palette.primary.main,
    },
  },
  railHomeBtnActive: {
    backgroundColor: theme.palette.primary.main + "1F",
    color: theme.palette.primary.dark || theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "26",
    },
  },
  railToggleBtn: {
    color: tones.textMuted,
    padding: "6px",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "12",
    },
  },
  contentCard: {
    flex: "1 1 auto",
    minWidth: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: tones.surfaceBg,
  },
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  // Nao usamos display:flex no CardHeader porque o CardIcon padrao da
  // aplicacao depende de `float: left` + `marginTop: -10px` para
  // sobrepor a borda superior do Card. Colocar flex no CardHeader anula
  // o float e o icone desce para o fluxo, desalinhando do padrao.
  // Em vez disso: CardHeader vira `position: relative` e o searchField
  // e posicionado absolutamente no canto superior direito, alinhado
  // com a baseline do titulo (que tem marginTop: 15px).
  cardHeaderRel: {
    position: "relative",
  },
  // Espelha 1:1 o estilo do filtro de busca por nome de InputsV3.js
  // (linha 1650): standard variant, disableUnderline, fundo cinza claro
  // arredondado, fonte 0.85rem. Removido o icone de busca por decisao
  // explicita do usuario.
  searchField: {
    position: "absolute",
    top: "50%",
    right: "-4px",
    transform: "translateY(-50%)",
    width: "300px",
  },
  // ----- Rail (esquerda): scroll proprio + wheel nativo -----
  // Vive dentro de `railCard` (que ja' e um Card branco com sombra)
  // entao nao precisa mais de background nem borderRight.
  rail: {
    flex: "1 1 auto",
    minHeight: 0,
    padding: "10px 0",
    boxSizing: "border-box",
  },
  railTopic: {
    // marginBottom: "2px",
  },
  railTopicHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 6px",
    cursor: "pointer",
    color: tones.textSecondary,
    fontSize: "14px",
    fontWeight: 400,
    userSelect: "none",
    borderLeft: "3px solid transparent",
    transition:
      "background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease",
    "&:hover": {
      backgroundColor: tones.surfaceSoft,
      color: tones.textPrimary,
    },
  },
  railTopicHeaderActive: {
    backgroundColor: theme.palette.primary.main + "1F",
    color: theme.palette.primary.dark || theme.palette.primary.main,
    borderLeftColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "26",
      color: theme.palette.primary.dark || theme.palette.primary.main,
    },
  },
  railChevron: {
    width: "18px",
    height: "18px",
    color: tones.textFaint,
    transition: "transform 0.18s ease",
    flexShrink: 0,
  },
  railChevronExpanded: {
    transform: "rotate(90deg)",
  },
  railTopicPosition: {
    color: tones.textFaint,
    fontSize: "11px",
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    width: "16px",
    flexShrink: 0,
  },
  railTopicPositionActive: {
    color: theme.palette.primary.main,
  },
  railTopicTitle: {
    flex: 1,
    lineHeight: 1.3,
  },
  railSubs: {
    listStyle: "none",
    margin: 0,
    padding: "2px 0 4px 0",
    maxHeight: 0,
    overflow: "hidden",
    transition: "max-height 0.25s ease",
  },
  railSubsExpanded: {
    maxHeight: "400px",
  },
  railSub: {
    padding: "6px 12px 6px 30px",
    cursor: "pointer",
    color: tones.textMuted,
    fontSize: "12px",
    lineHeight: 1.4,
    borderLeft: "3px solid transparent",
    transition:
      "background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      backgroundColor: tones.surfaceSoft,
      color: tones.textSecondary,
    },
  },
  railSubActive: {
    backgroundColor: theme.palette.primary.main + "1F",
    color: theme.palette.primary.dark || theme.palette.primary.main,
    borderLeftColor: theme.palette.primary.main,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "26",
      color: theme.palette.primary.dark || theme.palette.primary.main,
    },
  },
  railSubPosition: {
    color: tones.textFaint,
    fontSize: "11px",
    fontVariantNumeric: "tabular-nums",
    width: "22px",
    flexShrink: 0,
  },
  railSubPositionActive: {
    color: theme.palette.primary.main,
  },

  // ----- Conteudo (direita): scroll proprio (e' o root do IO) -----
  // Vive dentro de `contentCard` (que ja carrega o fundo cinza).
  contentScroll: {
    flex: "1 1 auto",
    minHeight: 0,
    boxSizing: "border-box",
  },
  content: {
    padding: "12px",
    minWidth: 0,
    boxSizing: "border-box",
  },
  topicHeader: {
    marginBottom: "20px",
  },
  topicEyebrow: {
    color: tones.textFaint,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: 600,
    marginLeft: "6px",
  },
  topicTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: tones.textPrimary,
    margin: "0 6px",
  },
  subtopicBlock: {
    backgroundColor: tones.surfaceCard,
    border: `1px solid ${tones.borderLine}`,
    borderRadius: "8px",
    padding: "22px 22px 8px 22px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    scrollMarginTop: "100px",
  },
  subtopicHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "10px",
    paddingBottom: "10px",
    borderBottom: `1px solid ${tones.borderLine}`,
  },
  subtopicTitle: {
    fontSize: "17px",
    fontWeight: 600,
    color: tones.textPrimary,
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  subtopicCounter: {
    color: tones.textFaint,
    fontSize: "14px",
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    marginRight: "4px",
  },
  // Pill de "Atualizado em" — herdado do prototipo HTML, sem dot:
  // fundo cinza claro, borda fina, cantos arredondados, tipografia
  // sobria. Esta a direita do header do SubTopic, vertical-aligned com
  // o titulo.
  subtopicUpdated: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: tones.surfaceSoft,
    border: `1px solid ${tones.borderLine}`,
    borderRadius: "999px",
    padding: "2px 10px",
    color: tones.textMuted,
    fontSize: "11px",
    fontWeight: 400,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
    marginTop: "-6px",
  },
  // Bloco a direita do titulo: pill drift + pill data + botao regenerar.
  subtopicHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  subtopicStalePill: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#fef3c7",
    color: "#888888",
    borderRadius: "999px",
    padding: "2px 10px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: "help",
    flexShrink: 0,
    marginTop: "-2px",
  },
  subtopicRegenBtn: {
    textTransform: "none",
    fontSize: "12px",
    padding: "2px 12px",
    minHeight: "28px",
    borderColor: tones.borderLine,
    color: tones.textSecondary,
    marginTop: "-5px",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "0A",
    },
  },
  // Badge no header do Topic (rail): "N subtopicos com drift".
  railStaleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f59e0b",
    color: "#fff",
    borderRadius: "999px",
    padding: "0 4px",
    minWidth: "16px",
    height: "16px",
    fontSize: "10px",
    fontWeight: 700,
    marginLeft: "auto",
    flexShrink: 0,
  },
  // Badge "L" para Topic com manual_lock.
  railLockBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tones.surfaceSoft,
    border: `1px solid ${tones.borderLine}`,
    color: tones.textMuted,
    borderRadius: "4px",
    padding: "0 4px",
    height: "16px",
    fontSize: "10px",
    fontWeight: 700,
    marginLeft: "4px",
    flexShrink: 0,
  },
  // Dot ambar a direita do SubTopic na rail (indica drift).
  railSubStaleDot: {
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#f59e0b",
    flexShrink: 0,
  },

  // ----------------------------------------------------------------- //
  // Modal de preview de regen — alinhado ao padrao da aplicacao:       //
  // ModalHeader (faixa escura com strip da cor do cliente) + corpo     //
  // em fundo cinza claro + footer com botoes ``CustomButtons/Button``. //
  // ----------------------------------------------------------------- //
  // ----- Modal de regen V3 (editor conversacional) -----
  // Layout: header + version-bar + body (conteudo + chat aside) + footer.
  // Largura calibrada para painel de conteudo confortavel (~720-900px)
  // + chat lateral fixo (~380px). Em telas estreitas a aside encolhe.
  regenModalPaper: {
    width: "min(1280px, 95vw)",
    maxWidth: "95vw",
    height: "min(88vh, 940px)",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 6,
  },
  regenModalError: {
    backgroundColor: "#fee2e2",
    color: "#7f1d1d",
    padding: "8px 20px",
    borderBottom: `1px solid #fca5a5`,
    fontSize: "12px",
    flexShrink: 0,
  },
  regenModalContent: {
    flex: "1 1 auto",
    minHeight: 0,
    padding: "0 !important",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tones.surfaceBg,
    overflow: "hidden",
  },
  // ----- Barra de versoes (tabs) -----
  regenVersionBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    backgroundColor: tones.surfaceCard,
    borderBottom: `1px solid ${tones.borderLine}`,
    flexShrink: 0,
    flexWrap: "wrap",
  },
  regenVersionTab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    border: `1px solid ${tones.borderLine}`,
    backgroundColor: tones.surfaceSoft,
    borderRadius: 999,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 500,
    color: tones.textSecondary,
    cursor: "pointer",
    transition:
      "background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease",
    "&:hover": {
      backgroundColor: "#fff",
      borderColor: tones.textFaint,
    },
  },
  regenVersionTabActive: {
    backgroundColor: theme.palette.primary.main + "1F",
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.dark || theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "26",
      borderColor: theme.palette.primary.main,
    },
  },
  regenVersionTabCurrent: {
    fontStyle: "italic",
  },
  regenVersionTabLabel: {
    lineHeight: 1.2,
  },
  regenVersionTabClose: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "50%",
    color: tones.textMuted,
    cursor: "pointer",
    transition: "background-color 0.12s ease, color 0.12s ease",
    "&:hover": {
      backgroundColor: "#fca5a5",
      color: "#7f1d1d",
    },
  },
  regenVersionGenerating: {
    marginLeft: "auto",
    color: tones.textMuted,
    fontSize: 11,
    fontStyle: "italic",
  },
  // ----- Body: conteudo (esq) + chat aside (dir) -----
  regenBodyLayout: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: 1,
    backgroundColor: tones.borderLine,
    flex: "1 1 auto",
    minHeight: 0,
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr 320px",
    },
  },
  regenContentPane: {
    backgroundColor: tones.surfaceCard,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  regenContentScroll: {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
  },
  regenContentInner: {
    padding: "20px 24px",
    fontSize: 13,
    lineHeight: 1.55,
    color: tones.textSecondary,
  },
  // Toolbar de zoom acima do scroll: A- · 100% · A+. Discreta, no
  // canto direito, sticky ao topo do pane.
  regenZoomToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
    padding: "4px 8px",
    borderBottom: `1px solid ${tones.borderLine}`,
    backgroundColor: tones.surfaceSoft,
    flexShrink: 0,
  },
  regenZoomBtn: {
    padding: 4,
    color: tones.textMuted,
    "&:hover:not(:disabled)": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "12",
    },
    "&:disabled": {
      color: tones.textFaint,
      opacity: 0.55,
    },
  },
  regenZoomLabel: {
    border: "none",
    background: "transparent",
    color: tones.textMuted,
    fontSize: 11,
    fontFamily: "inherit",
    fontVariantNumeric: "tabular-nums",
    minWidth: 36,
    textAlign: "center",
    padding: "2px 6px",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "12",
      color: theme.palette.primary.main,
    },
  },
  regenContentPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: tones.textFaint,
    fontSize: 13,
    padding: 24,
    textAlign: "center",
    lineHeight: 1.5,
  },
  // ----- Aside de chat -----
  regenChatAside: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: tones.surfaceCard,
    minHeight: 0,
  },
  regenChatHistory: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  regenChatHint: {
    color: tones.textMuted,
    fontSize: 12,
    lineHeight: 1.5,
    padding: "8px 4px",
    fontStyle: "italic",
  },
  regenChatBubble: {
    maxWidth: "92%",
    padding: "7px 11px",
    borderRadius: 10,
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  regenChatBubbleText: {
    margin: 0,
  },
  regenChatBubbleMeta: {
    marginTop: 4,
    fontSize: 10,
    color: tones.textFaint,
    fontVariantNumeric: "tabular-nums",
  },
  regenChatBubbleUser: {
    alignSelf: "flex-end",
    backgroundColor: theme.palette.primary.main + "1F",
    color: theme.palette.primary.dark || theme.palette.primary.main,
    borderBottomRightRadius: 2,
  },
  regenChatBubbleAssistant: {
    alignSelf: "flex-start",
    backgroundColor: tones.surfaceSoft,
    color: tones.textSecondary,
    border: `1px solid ${tones.borderLine}`,
    borderBottomLeftRadius: 2,
  },
  regenChatBubbleTyping: {
    fontStyle: "italic",
    opacity: 0.85,
  },
  regenChatInputWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "8px 12px 12px 12px",
    borderTop: `1px solid ${tones.borderLine}`,
    flexShrink: 0,
    backgroundColor: tones.surfaceSoft,
  },
  regenChatToolRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  regenPresetTrigger: {
    padding: "3px 8px",
    borderRadius: 6,
    fontSize: 12,
    color: tones.textSecondary,
    backgroundColor: "#fff",
    border: `1px solid ${tones.borderLine}`,
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "0F",
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 14,
      marginRight: 2,
    },
  },
  regenPresetTriggerLabel: {
    marginLeft: 2,
    textTransform: "none",
    fontWeight: 500,
  },
  regenChatField: {
    "& .MuiOutlinedInput-root": {
      fontSize: 11.5,
      backgroundColor: "#fff",
      padding: "6px 10px",
      lineHeight: 1.4,
    },
  },
  regenChatSendRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 8,
  },
  regenChatHelpStack: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },
  regenChatHelp: {
    color: tones.textFaint,
    fontSize: 10.5,
    lineHeight: 1.3,
  },
  // Linha "Iterando sobre vN" — destaca em qual versao da sessao a
  // proxima chamada vai se basear. Cor levemente mais forte que o help
  // para indicar que e' o estado ativo, sem virar alarme visual.
  regenChatIterating: {
    color: tones.textMuted,
    fontSize: 10.5,
    lineHeight: 1.3,
    fontWeight: 500,
  },
  // Variante quando o usuario clicou em "Refazer do zero": destaca em
  // ambar para que o user nao perca a mudanca de modo (vai descartar
  // o baseline na proxima chamada).
  regenChatIteratingFresh: {
    color: "#b45c00",
    fontWeight: 600,
  },
  regenModalActions: {
    padding: "10px 16px",
    borderTop: `1px solid ${tones.borderLine}`,
    backgroundColor: "#fff",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  regenFooterHint: {
    flex: "1 1 auto",
    color: tones.textFaint,
    fontSize: 11,
    lineHeight: 1.4,
  },
  empty: {
    padding: "40px",
    color: tones.textFaint,
    textAlign: "center",
    fontSize: "14px",
  },

  // ----- Tela inicial (boas-vindas) -----
  welcome: {
    maxWidth: "880px",
    margin: "0 auto",
    padding: "clamp(24px, 5vw, 56px) clamp(16px, 4vw, 40px) 48px",
    boxSizing: "border-box",
  },
  welcomeHero: {
    textAlign: "center",
    marginBottom: "28px",
  },
  welcomeTitle: {
    fontSize: "clamp(22px, 3vw, 30px)",
    fontWeight: 600,
    color: tones.textPrimary,
    margin: 0,
    lineHeight: 1.25,
  },
  welcomeCards: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
    marginBottom: "36px",
    // Em telas estreitas, empilha em coluna unica.
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  welcomeCard: {
    backgroundColor: tones.surfaceCard,
    border: `1px solid ${tones.borderLine}`,
    borderRadius: "10px",
    padding: "22px 20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  welcomeCardIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    backgroundColor: theme.palette.primary.main + "1A",
    color: theme.palette.primary.main,
  },
  welcomeCardTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: tones.textPrimary,
  },
  welcomeCardText: {
    fontSize: "13.5px",
    lineHeight: 1.55,
    color: tones.textMuted,
  },

  // =============================================
  // ESTILOS DE CONTEUDO — espelham documentationStyle (V2)
  // =============================================
  subtopic: {
    maxWidth: "100%",
    overflowX: "auto",
    boxSizing: "border-box",
    "& .doc-section-title": {
      fontSize: "15px",
      fontWeight: 600,
      color: grayColor[2],
      marginTop: "24px",
      marginBottom: "6px",
      paddingBottom: "4px",
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      display: "inline-block",
      "&:first-child": {
        marginTop: "8px",
      },
    },
    "& .doc-subsection-title": {
      fontSize: "14px",
      fontWeight: 600,
      color: grayColor[3],
      marginTop: "20px",
      marginBottom: "10px",
    },
    "& .doc-formula": {
      backgroundColor: "#353534",
      color: "#d4d4d4",
      padding: "16px clamp(12px, 2vw, 20px)",
      borderRadius: "6px",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      fontSize: "clamp(11px, 1.2vw, 13px)",
      lineHeight: "1.7",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      whiteSpace: "pre",
      margin: "12px 0",
      border: "1px solid #444",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      maxWidth: "100%",
      boxSizing: "border-box",
      "& *": {
        fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      },
      "& .formula-keyword": { color: "#569cd6" },
      "& .formula-function": { color: "#dcdcaa" },
      "& .formula-comment": { color: "#6a9955", fontStyle: "italic" },
      "& .formula-variable": { color: "#9cdcfe" },
      "& .formula-operator": { color: "#d4d4d4" },
    },
    "& .doc-table": {
      display: "block",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      borderCollapse: "collapse",
      margin: "16px 0",
      fontSize: "14px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      borderRadius: "6px",
      "& thead": {
        backgroundColor: theme.palette.primary.main,
        "& th": {
          color: whiteColor,
          fontWeight: 600,
          padding: "8px clamp(8px, 1.5vw, 16px)",
          textAlign: "left",
          borderBottom: "none",
          whiteSpace: "nowrap",
        },
      },
      "& tbody": {
        "& tr": {
          backgroundColor: whiteColor,
          transition: "background-color 0.15s ease",
          "&:nth-child(even)": {
            backgroundColor: "#f8f9fa",
          },
          "&:hover": {
            backgroundColor: "#e9ecef",
          },
        },
        "& td": {
          padding: "6px clamp(8px, 1.5vw, 16px)",
          textAlign: "left",
          borderBottom: `1px solid #e9ecef`,
          verticalAlign: "top",
        },
      },
    },
    "& .doc-table-full": {
      width: "100%",
    },
    "& code": {
      backgroundColor: "#f1f3f4",
      color: "#c7254e",
      padding: "2px 6px",
      borderRadius: "4px",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      fontSize: "13px",
      fontWeight: 500,
    },
    "& .doc-steps": {
      listStyle: "none",
      paddingLeft: "0",
      counterReset: "step-counter",
      "& > li": {
        counterIncrement: "step-counter",
        paddingLeft: "36px",
        position: "relative",
        marginBottom: "12px",
        "&::before": {
          content: "counter(step-counter)",
          position: "absolute",
          left: "0",
          top: "0",
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          color: whiteColor,
          fontSize: "12px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },
    "& .doc-list": {
      listStyle: "none",
      paddingLeft: "0",
      "& > li": {
        paddingLeft: "20px",
        position: "relative",
        marginBottom: "8px",
        "&::before": {
          content: "''",
          position: "absolute",
          left: "0",
          top: "8px",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    "& .doc-highlight": {
      backgroundColor: "#e8f4fd",
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      padding: "12px 16px",
      borderRadius: "0 6px 6px 0",
      margin: "16px 0",
      "& p": { margin: "0" },
    },
    "& .doc-note": {
      backgroundColor: "#fff8e6",
      borderLeft: "4px solid #ffc107",
      padding: "12px 16px",
      borderRadius: "0 6px 6px 0",
      margin: "16px 0",
      fontSize: "13px",
      "& p": { margin: "0" },
    },
    "& .doc-badge": {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      "&.badge-input": { backgroundColor: "#e3f2fd", color: "#1565c0" },
      "&.badge-column": { backgroundColor: "#f3e5f5", color: "#7b1fa2" },
      "&.badge-filter": { backgroundColor: "#fff3e0", color: "#e65100" },
    },
    "& .doc-dimension-grid": {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "16px",
      margin: "16px 0",
    },
    "& .doc-dimension-card": {
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      padding: "16px",
      border: "1px solid #e9ecef",
      "& .dimension-title": {
        fontWeight: 600,
        color: grayColor[2],
        marginBottom: "8px",
        fontSize: "14px",
      },
      "& .dimension-list": {
        listStyle: "none",
        padding: "0",
        margin: "0",
        "& li": {
          fontSize: "13px",
          padding: "4px 0",
          color: grayColor[2],
          borderBottom: "1px solid #e9ecef",
          "&:last-child": { borderBottom: "none" },
          "& strong": { color: grayColor[1] },
        },
      },
    },
    "& .doc-divider": {
      height: "1px",
      backgroundColor: "#e9ecef",
      margin: "24px 0",
      border: "none",
    },
    "& p, & ol, & ul, & li": {
      lineHeight: 1.45,
      marginTop: "10px",
      marginBottom: "10px",
    },
    "& ul, & ol": {
      paddingLeft: "1.5em",
    },
  },
});

export default documentationV3Style;
